import yaml from 'js-yaml';
import * as api from './api';

export async function validateOpenAPISpec(specContent, format = 'yaml') {
  try {
    const parsed = format === 'json' ? JSON.parse(specContent) : yaml.load(specContent);
    let errors = [];
    
    // Try Spectral validator if logged in
    const token = localStorage.getItem('auth-token');
    if (token && token !== 'guest-token') {
      try {
        const ibmResults = await api.validateSpec(specContent, format);
        if (ibmResults.errors) {
          errors = errors.concat(ibmResults.errors.map(err => {
            const fixFn = createAutoFix(err, parsed, specContent, format);
            return {
              message: err.message,
              type: 'error',
              line: err.line || 1,
              path: err.path?.join('.') || '',
              code: err.code,
              ...(fixFn && { fix: fixFn })
            };
          }));
        }
        if (ibmResults.warnings) {
          errors = errors.concat(ibmResults.warnings.map(warn => {
            const msg = warn.message.toLowerCase();
            const isRecommendation = msg.includes('should') || msg.includes('recommend') || msg.includes('consider') || msg.includes('best practice');
            const fixFn = createAutoFix(warn, parsed, specContent, format);
            return {
              message: warn.message,
              type: isRecommendation ? 'info' : 'warning',
              line: warn.line || 1,
              path: warn.path?.join('.') || '',
              code: warn.code,
              ...(fixFn && { fix: fixFn })
            };
          }));
        }
      } catch (ibmErr) {
        // IBM validator failed, continue with basic validation
      }
    }
    
    // Enhanced validation rules
    addBestPracticeChecks(parsed, errors, specContent, format);
    
    // OpenAPI version validation
    if (!parsed || typeof parsed !== 'object') {
      errors.push({ 
        message: 'Invalid OpenAPI document structure', 
        type: 'error',
        line: 1
      });
      return errors;
    }
    
    if (!parsed.openapi) {
      errors.push({ 
        message: 'Missing required "openapi" field - must specify OpenAPI version', 
        type: 'error',
        line: 1,
        fix: () => 'openapi: 3.0.0\n' + specContent
      });
    } else if (!parsed.openapi.toString().startsWith('3.0')) {
      errors.push({ 
        message: `Invalid OpenAPI version "${parsed.openapi}" - must be 3.0.x`, 
        type: 'error',
        line: findLineNumber(specContent, 'openapi') || 1,
        fix: () => addOrUpdateLine(specContent, 'openapi', 'openapi: 3.0.0')
      });
    }
    
    // Info section validation
    if (!parsed.info) {
      errors.push({ 
        message: 'Missing required "info" section', 
        type: 'error',
        line: 1,
        fix: () => addInfoSection(specContent)
      });
    } else {
      if (typeof parsed.info !== 'object') {
        errors.push({ 
          message: 'Field "info" must be an object', 
          type: 'error',
          line: findLineNumber(specContent, 'info') || 1
        });
      } else {
        if (!parsed.info.title) {
          errors.push({ 
            message: 'Missing required field "info.title"', 
            type: 'error',
            line: findLineNumber(specContent, 'info') || 1,
            fix: () => addToInfoSection(specContent, 'title', 'My API')
          });
        }
        if (!parsed.info.version) {
          errors.push({ 
            message: 'Missing required field "info.version"', 
            type: 'error',
            line: findLineNumber(specContent, 'info') || 1,
            fix: () => addToInfoSection(specContent, 'version', '1.0.0')
          });
        }
      }
    }
    
    // Parameter name validation
    if (parsed.paths && typeof parsed.paths === 'object') {
      Object.keys(parsed.paths).forEach(path => {
        const pathItem = parsed.paths[path];
        if (typeof pathItem === 'object') {
          const validMethods = ['get', 'post', 'put', 'delete', 'patch', 'options', 'head', 'trace'];
          Object.keys(pathItem).forEach(method => {
            if (validMethods.includes(method)) {
              const operation = pathItem[method];
              if (operation && operation.parameters) {
                operation.parameters.forEach((param, idx) => {
                  if (!param.name || param.name.trim() === '') {
                    errors.push({ 
                      message: `Parameter #${idx + 1} in ${method.toUpperCase()} ${path} has no name`, 
                      type: 'error',
                      line: findLineNumber(specContent, method) || 1
                    });
                  }
                  if (param.in === 'path' && !path.includes(`{${param.name}}`)) {
                    errors.push({ 
                      message: `Path parameter "${param.name}" not found in path "${path}" - should be "${path}/{${param.name}}"`, 
                      type: 'error',
                      line: findLineNumber(specContent, path) || 1
                    });
                  }
                });
              }
            }
          });
        }
      });
    }
    
    // Paths validation
    if (!parsed.paths) {
      errors.push({ 
        message: 'Missing required "paths" section', 
        type: 'error',
        line: specContent.split('\n').length,
        fix: () => ensurePathsSection(specContent)
      });
    } else if (typeof parsed.paths !== 'object') {
      errors.push({ 
        message: 'Field "paths" must be an object', 
        type: 'error',
        line: findLineNumber(specContent, 'paths') || 1
      });
    } else {
      // Validate each path
      Object.keys(parsed.paths).forEach(path => {
        if (!path.startsWith('/')) {
          errors.push({ 
            message: `Path "${path}" must start with "/"`, 
            type: 'error',
            line: findLineNumber(specContent, path) || 1,
            fix: () => fixPathSlash(specContent, path)
          });
        }
        
        const pathItem = parsed.paths[path];
        if (typeof pathItem === 'object') {
          // Validate operations
          const validMethods = ['get', 'post', 'put', 'delete', 'patch', 'options', 'head', 'trace'];
          Object.keys(pathItem).forEach(method => {
            if (!validMethods.includes(method) && !['parameters', 'summary', 'description', '$ref', 'servers'].includes(method)) {
              errors.push({ 
                message: `Invalid HTTP method "${method}" in path "${path}"`, 
                type: 'error',
                line: findLineNumber(specContent, method) || 1
              });
            }
            
            // Validate operation object
            if (validMethods.includes(method)) {
              const operation = pathItem[method];
              if (operation && typeof operation !== 'object') {
                errors.push({ 
                  message: `Operation "${method}" in path "${path}" must be an object`, 
                  type: 'error',
                  line: findLineNumber(specContent, method) || 1
                });
              } else if (operation && !operation.responses) {
                errors.push({ 
                  message: `Operation "${method.toUpperCase()} ${path}" missing required "responses" field`, 
                  type: 'error',
                  line: findLineNumber(specContent, method) || 1,
                  fix: () => addResponsesToOperation(specContent, path, method)
                });
              }
            }
          });
        }
      });
    }
    
    // Components validation
    if (parsed.components) {
      if (typeof parsed.components !== 'object') {
        errors.push({ 
          message: 'Field "components" must be an object', 
          type: 'error',
          line: findLineNumber(specContent, 'components') || 1
        });
      } else {
        // Validate schemas
        if (parsed.components.schemas) {
          Object.keys(parsed.components.schemas).forEach(schemaName => {
            const schema = parsed.components.schemas[schemaName];
            if (!schema.type && !schema.$ref && !schema.allOf && !schema.oneOf && !schema.anyOf) {
              errors.push({
                message: `Schema "${schemaName}" missing type definition`,
                type: 'error',
                line: findLineNumber(specContent, schemaName) || 1
              });
            }
          });
        }
        
        // Validate security schemes
        if (parsed.components.securitySchemes) {
          Object.keys(parsed.components.securitySchemes).forEach(schemeName => {
            const scheme = parsed.components.securitySchemes[schemeName];
            if (!scheme.type) {
              errors.push({
                message: `Security scheme "${schemeName}" missing required "type" field`,
                type: 'error',
                line: findLineNumber(specContent, schemeName) || 1
              });
            } else if (scheme.type === 'apiKey' && (!scheme.name || !scheme.in)) {
              errors.push({
                message: `API Key security scheme "${schemeName}" missing "name" or "in" field`,
                type: 'error',
                line: findLineNumber(specContent, schemeName) || 1
              });
            } else if (scheme.type === 'http' && !scheme.scheme) {
              errors.push({
                message: `HTTP security scheme "${schemeName}" missing "scheme" field`,
                type: 'error',
                line: findLineNumber(specContent, schemeName) || 1
              });
            } else if (scheme.type === 'oauth2' && !scheme.flows) {
              errors.push({
                message: `OAuth2 security scheme "${schemeName}" missing "flows" field`,
                type: 'error',
                line: findLineNumber(specContent, schemeName) || 1
              });
            } else if (scheme.type === 'openIdConnect' && !scheme.openIdConnectUrl) {
              errors.push({
                message: `OpenID Connect security scheme "${schemeName}" missing "openIdConnectUrl" field`,
                type: 'error',
                line: findLineNumber(specContent, schemeName) || 1
              });
            }
          });
        }
      }
    }
    
    // Response status codes validation
    if (parsed.paths && typeof parsed.paths === 'object') {
      Object.keys(parsed.paths).forEach(path => {
        const pathItem = parsed.paths[path];
        if (typeof pathItem === 'object') {
          const validMethods = ['get', 'post', 'put', 'delete', 'patch', 'options', 'head', 'trace'];
          Object.keys(pathItem).forEach(method => {
            if (validMethods.includes(method)) {
              const operation = pathItem[method];
              if (operation && operation.responses) {
                Object.keys(operation.responses).forEach(code => {
                  if (code !== 'default' && !/^[1-5]\d{2}$|^[1-5]XX$/.test(code)) {
                    errors.push({
                      message: `Invalid response code "${code}" in ${method.toUpperCase()} ${path} - must be 3-digit HTTP status or pattern like "2XX"`,
                      type: 'error',
                      line: findLineNumber(specContent, code) || 1
                    });
                  }
                  
                  const response = operation.responses[code];
                  if (!response.description) {
                    errors.push({
                      message: `Response ${code} in ${method.toUpperCase()} ${path} missing required "description" field`,
                      type: 'error',
                      line: findLineNumber(specContent, code) || 1
                    });
                  }
                });
              }
              
              // Validate parameters
              if (operation.parameters) {
                operation.parameters.forEach((param, idx) => {
                  if (!param.name) {
                    errors.push({
                      message: `Parameter #${idx + 1} in ${method.toUpperCase()} ${path} missing required "name" field`,
                      type: 'error',
                      line: findLineNumber(specContent, method) || 1
                    });
                  }
                  if (!param.in) {
                    errors.push({
                      message: `Parameter "${param.name || idx + 1}" in ${method.toUpperCase()} ${path} missing required "in" field`,
                      type: 'error',
                      line: findLineNumber(specContent, method) || 1
                    });
                  } else if (!['query', 'header', 'path', 'cookie'].includes(param.in)) {
                    errors.push({
                      message: `Parameter "${param.name}" has invalid "in" value "${param.in}" - must be query, header, path, or cookie`,
                      type: 'error',
                      line: findLineNumber(specContent, param.name) || 1
                    });
                  }
                  if (!param.schema && !param.content) {
                    errors.push({
                      message: `Parameter "${param.name}" in ${method.toUpperCase()} ${path} missing "schema" or "content" field`,
                      type: 'error',
                      line: findLineNumber(specContent, param.name) || 1
                    });
                  }
                  if (param.in === 'path' && !param.required) {
                    errors.push({
                      message: `Path parameter "${param.name}" in ${method.toUpperCase()} ${path} must have "required: true"`,
                      type: 'error',
                      line: findLineNumber(specContent, param.name) || 1
                    });
                  }
                });
              }
              
              // Validate request body
              if (operation.requestBody) {
                if (!operation.requestBody.content) {
                  errors.push({
                    message: `Request body in ${method.toUpperCase()} ${path} missing required "content" field`,
                    type: 'error',
                    line: findLineNumber(specContent, method) || 1
                  });
                } else if (Object.keys(operation.requestBody.content).length === 0) {
                  errors.push({
                    message: `Request body in ${method.toUpperCase()} ${path} has empty "content" object`,
                    type: 'error',
                    line: findLineNumber(specContent, method) || 1
                  });
                }
              }
            }
          });
        }
      });
    }
    
    // Servers validation
    if (parsed.servers) {
      if (!Array.isArray(parsed.servers)) {
        errors.push({
          message: 'Field "servers" must be an array',
          type: 'error',
          line: findLineNumber(specContent, 'servers') || 1
        });
      } else {
        parsed.servers.forEach((server, idx) => {
          if (!server.url) {
            errors.push({
              message: `Server #${idx + 1} missing required "url" field`,
              type: 'error',
              line: findLineNumber(specContent, 'servers') || 1
            });
          } else if (!server.url.startsWith('http://') && !server.url.startsWith('https://') && !server.url.startsWith('/') && !server.url.startsWith('{')) {
            errors.push({
              message: `Server URL "${server.url}" should start with http://, https://, /, or be a variable`,
              type: 'warning',
              line: findLineNumber(specContent, server.url) || 1
            });
          }
        });
      }
    }
    
    // Tags validation
    if (parsed.tags) {
      if (!Array.isArray(parsed.tags)) {
        errors.push({
          message: 'Field "tags" must be an array',
          type: 'error',
          line: findLineNumber(specContent, 'tags') || 1
        });
      } else {
        const tagNames = new Set();
        parsed.tags.forEach((tag, idx) => {
          if (!tag.name) {
            errors.push({
              message: `Tag #${idx + 1} missing required "name" field`,
              type: 'error',
              line: findLineNumber(specContent, 'tags') || 1
            });
          } else if (tagNames.has(tag.name)) {
            errors.push({
              message: `Duplicate tag name "${tag.name}"`,
              type: 'error',
              line: findLineNumber(specContent, tag.name) || 1
            });
          } else {
            tagNames.add(tag.name);
          }
        });
      }
    }
    
    // External docs validation
    if (parsed.externalDocs) {
      if (!parsed.externalDocs.url) {
        errors.push({
          message: 'External docs missing required "url" field',
          type: 'error',
          line: findLineNumber(specContent, 'externalDocs') || 1
        });
      }
    }
    
    // Security validation
    if (parsed.security) {
      if (!Array.isArray(parsed.security)) {
        errors.push({
          message: 'Field "security" must be an array',
          type: 'error',
          line: findLineNumber(specContent, 'security') || 1
        });
      } else {
        parsed.security.forEach((requirement, idx) => {
          Object.keys(requirement).forEach(schemeName => {
            if (!parsed.components?.securitySchemes?.[schemeName]) {
              errors.push({
                message: `Security requirement references undefined scheme "${schemeName}"`,
                type: 'error',
                line: findLineNumber(specContent, 'security') || 1
              });
            }
          });
        });
      }
    }
    
    return errors;
  } catch (error) {
    if (format === 'json') {
      const match = error.message.match(/position (\d+)/);
      const position = match ? parseInt(match[1]) : 0;
      const lines = specContent.substring(0, position).split('\n');
      const line = lines.length;
      const column = lines[lines.length - 1].length + 1;
      
      let message = error.message;
      if (message.includes('Unexpected token')) {
        message = 'Invalid JSON syntax - check for missing commas, quotes, or brackets';
      } else if (message.includes('Unexpected end')) {
        message = 'Incomplete JSON - missing closing bracket or brace';
      } else if (message.includes('Unexpected string')) {
        message = 'Invalid JSON - missing comma between properties';
      }
      
      return [{ 
        message: `JSON error at line ${line}, column ${column}: ${message}`, 
        type: 'error',
        line: line,
        column: column
      }];
    }
    const line = error.mark ? error.mark.line + 1 : 1;
    const column = error.mark ? error.mark.column + 1 : 1;
    return [{ 
      message: `YAML error at line ${line}, column ${column}: ${getSimpleErrorMessage(error.reason)}`, 
      type: 'error',
      line: line,
      column: column
    }];
  }
}

function findLineNumber(content, searchTerm) {
  const lines = content.split('\n');
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].includes(searchTerm + ':') || lines[i].includes(searchTerm)) {
      return i + 1;
    }
  }
  return null;
}

function getSimpleErrorMessage(reason) {
  if (!reason) return 'invalid syntax';
  if (reason.includes('bad indentation')) {
    return 'incorrect indentation - use 2 spaces per level';
  }
  if (reason.includes('mapping')) {
    return 'invalid structure - check colons and indentation';
  }
  if (reason.includes('sequence')) {
    return 'invalid list format - check dashes and indentation';
  }
  if (reason.includes('duplicate')) {
    return 'duplicate key - each key must be unique';
  }
  if (reason.includes('expected')) {
    return 'unexpected character or missing value';
  }
  if (reason.includes('end of the stream')) {
    return 'incomplete document - check for missing values';
  }
  if (reason.includes('could not find expected')) {
    return 'missing closing character or incorrect nesting';
  }
  if (reason.includes('did not find expected key')) {
    return 'invalid key-value pair - check colons and spacing';
  }
  return reason;
}

function addOrUpdateLine(content, key, newLine) {
  const lines = content.split('\n');
  const keyIndex = lines.findIndex(line => line.includes(key + ':'));
  
  if (keyIndex !== -1) {
    lines[keyIndex] = newLine;
  } else {
    lines.unshift(newLine);
  }
  
  return lines.join('\n');
}

function addInfoSection(content) {
  const lines = content.split('\n');
  const openapiIndex = lines.findIndex(line => line.trim().startsWith('openapi:'));
  
  const infoSection = [
    'info:',
    '  title: My API',
    '  version: 1.0.0'
  ];
  
  if (openapiIndex !== -1) {
    lines.splice(openapiIndex + 1, 0, ...infoSection);
  } else {
    lines.unshift(...infoSection);
  }
  return lines.join('\n');
}

function addToInfoSection(content, key, value) {
  const lines = content.split('\n');
  const infoIndex = lines.findIndex(line => line.trim().startsWith('info:'));
  
  if (infoIndex !== -1) {
    let insertIndex = infoIndex + 1;
    while (insertIndex < lines.length && lines[insertIndex].match(/^\s\s[^\s]/)) {
      insertIndex++;
    }
    lines.splice(insertIndex, 0, `  ${key}: ${value}`);
  }
  
  return lines.join('\n');
}

function ensurePathsSection(content) {
  const lines = content.split('\n');
  const pathsIndex = lines.findIndex(line => line.trim().startsWith('paths:'));
  
  if (pathsIndex === -1) {
    // Add paths section at the end
    if (lines[lines.length - 1].trim() !== '') {
      lines.push('');
    }
    lines.push('paths: {}');
  }
  
  return lines.join('\n');
}

function fixPathSlash(content, path) {
  return content.replace(new RegExp(`^(\\s*)${path}:`, 'm'), `$1/${path}:`);
}

function addResponsesToOperation(content, path, method) {
  const lines = content.split('\n');
  const pathIndex = lines.findIndex(line => line.includes(`${path}:`));
  
  if (pathIndex !== -1) {
    const methodIndex = lines.findIndex((line, i) => i > pathIndex && line.includes(`${method}:`));
    if (methodIndex !== -1) {
      let insertIndex = methodIndex + 1;
      while (insertIndex < lines.length && lines[insertIndex].match(/^\s{6,}[^\s]/)) {
        insertIndex++;
      }
      lines.splice(insertIndex, 0, '      responses:', '        200:', '          description: Success');
    }
  }
  
  return lines.join('\n');
}

function addBestPracticeChecks(parsed, errors, specContent, format) {
  // Check for missing descriptions
  if (parsed.info && !parsed.info.description) {
    errors.push({
      message: 'API should have a description in info section',
      type: 'info',
      line: findLineNumber(specContent, 'info') || 1,
      fix: () => addToInfoSection(specContent, 'description', 'API description')
    });
  }
  
  // Check operations for missing summaries and descriptions
  if (parsed.paths) {
    Object.keys(parsed.paths).forEach(path => {
      const pathItem = parsed.paths[path];
      ['get', 'post', 'put', 'delete', 'patch'].forEach(method => {
        if (pathItem[method]) {
          const op = pathItem[method];
          if (!op.summary) {
            errors.push({
              message: `Operation ${method.toUpperCase()} ${path} should have a summary`,
              type: 'info',
              line: findLineNumber(specContent, method) || 1,
              fix: () => addOperationProperty(specContent, path, method, 'summary', 'Operation summary')
            });
          }
          if (!op.operationId) {
            errors.push({
              message: `Operation ${method.toUpperCase()} ${path} should have an operationId`,
              type: 'info',
              line: findLineNumber(specContent, method) || 1,
              fix: () => addOperationProperty(specContent, path, method, 'operationId', method + path.replace(/[^a-zA-Z0-9]/g, ''))
            });
          }
          if (op.parameters) {
            op.parameters.forEach((param, idx) => {
              if (!param.description) {
                errors.push({
                  message: `Parameter "${param.name}" in ${method.toUpperCase()} ${path} should have a description`,
                  type: 'info',
                  line: findLineNumber(specContent, param.name) || 1
                });
              }
            });
          }
        }
      });
    });
  }
  
  // Check schemas for missing descriptions
  if (parsed.components?.schemas) {
    Object.keys(parsed.components.schemas).forEach(schemaName => {
      const schema = parsed.components.schemas[schemaName];
      // Only flag if schema is an object and has no description
      // Skip if it's just a reference or if it has type/properties defined
      if (schema && typeof schema === 'object' && !schema.description && !schema.$ref && (schema.type || schema.properties || schema.allOf || schema.oneOf || schema.anyOf)) {
        errors.push({
          message: `Schema "${schemaName}" should have a description`,
          type: 'info',
          line: findLineNumber(specContent, schemaName) || 1,
          fix: () => addDescriptionAtPath(specContent, ['components', 'schemas', schemaName], format || 'yaml')
        });
      }
    });
  }
}

function addOperationProperty(content, path, method, property, value) {
  try {
    const parsed = yaml.load(content);
    if (parsed.paths?.[path]?.[method]) {
      parsed.paths[path][method][property] = value;
    }
    return yaml.dump(parsed, { indent: 2, noRefs: true });
  } catch (e) {
    return content;
  }
}

function createAutoFix(issue, parsed, specContent, format) {
  const msg = issue.message.toLowerCase();
  const path = issue.path || [];
  const code = issue.code || '';
  
  // Info contact
  if (code === 'info-contact' || (msg.includes('info') && msg.includes('contact'))) {
    return () => addContactInfo(specContent, format);
  }
  
  // Info license
  if (code === 'info-license' || (msg.includes('info') && msg.includes('license'))) {
    return () => addLicenseInfo(specContent, format);
  }
  
  // License URL
  if (code === 'license-url' || (msg.includes('license') && msg.includes('url'))) {
    return () => addLicenseUrl(specContent, format);
  }
  
  // OpenAPI tags
  if (code === 'openapi-tags' || (msg.includes('openapi') && msg.includes('tags'))) {
    return () => addTags(specContent, format);
  }
  
  // Operation tags
  if (code === 'operation-tags' || (msg.includes('operation') && msg.includes('tags') && path.length > 0)) {
    return () => addOperationTags(specContent, path, format);
  }
  
  // Tag description
  if (code === 'tag-description' || (msg.includes('tag') && msg.includes('description'))) {
    return () => addTagDescription(specContent, path, format);
  }
  
  // Operation description
  if (code === 'operation-description' || (msg.includes('operation') && msg.includes('description'))) {
    return () => addDescriptionAtPath(specContent, path, format);
  }
  
  // Operation operationId
  if (code === 'operation-operationId' || msg.includes('operationid')) {
    return () => addOperationIdAtPath(specContent, path, format);
  }
  
  // Parameter description
  if (code === 'oas3-parameter-description' || code === 'ibm-parameter-description' || (msg.includes('parameter') && msg.includes('description'))) {
    return () => addParameterDescriptionAtPath(specContent, path, format);
  }
  
  // Schema/Component description
  if (code === 'component-description' || code === 'ibm-schema-description' || code === 'ibm-property-description' || (msg.includes('schema') && msg.includes('description')) || (msg.includes('property') && msg.includes('description'))) {
    return () => addDescriptionAtPath(specContent, path, format);
  }
  
  // Path trailing slash
  if (code === 'path-keys-no-trailing-slash' || (msg.includes('path') && msg.includes('trailing slash'))) {
    return () => removeTrailingSlash(specContent, path, format);
  }
  
  // Server trailing slash
  if (code === 'oas3-server-trailing-slash' || (msg.includes('server') && msg.includes('trailing slash'))) {
    return () => removeServerTrailingSlash(specContent, path, format);
  }
  
  // API servers
  if (code === 'oas3-api-servers' || (msg.includes('servers') && msg.includes('array'))) {
    return () => addServers(specContent, format);
  }
  
  // Success response
  if (code === 'operation-success-response' || (msg.includes('success') && msg.includes('response'))) {
    return () => addSuccessResponse(specContent, path, format);
  }
  
  // Generic description fixes
  if (msg.includes('description')) {
    return () => addDescriptionAtPath(specContent, path, format);
  }
  
  // Generic summary fixes
  if (msg.includes('summary')) {
    return () => addSummaryAtPath(specContent, path, format);
  }
  
  // Generic example fixes
  if (msg.includes('example')) {
    return () => addExampleAtPath(specContent, path, format);
  }
  
  return null;
}

function addDescriptionAtPath(content, path, format) {
  try {
    const parsed = format === 'json' ? JSON.parse(content) : yaml.load(content);
    let obj = parsed;
    
    // Navigate to the target object
    for (let i = 0; i < path.length; i++) {
      if (!obj[path[i]]) return content;
      if (i === path.length - 1) {
        // We're at the target
        if (typeof obj[path[i]] === 'object' && !obj[path[i]].description) {
          obj[path[i]].description = `Description for ${path[i]}`;
        }
      } else {
        obj = obj[path[i]];
      }
    }
    
    return format === 'json' ? JSON.stringify(parsed, null, 2) : yaml.dump(parsed, { indent: 2, noRefs: true });
  } catch (e) {
    return content;
  }
}

function addSummaryAtPath(content, path, format) {
  try {
    const parsed = format === 'json' ? JSON.parse(content) : yaml.load(content);
    let obj = parsed;
    
    for (let i = 0; i < path.length; i++) {
      obj = obj[path[i]];
      if (!obj) return content;
    }
    
    if (typeof obj === 'object') {
      obj.summary = 'Summary for ' + path[path.length - 1];
    }
    
    return format === 'json' ? JSON.stringify(parsed, null, 2) : yaml.dump(parsed, { indent: 2, noRefs: true });
  } catch (e) {
    return content;
  }
}

function addOperationIdAtPath(content, path, format) {
  try {
    const parsed = format === 'json' ? JSON.parse(content) : yaml.load(content);
    let obj = parsed;
    
    for (let i = 0; i < path.length; i++) {
      obj = obj[path[i]];
      if (!obj) return content;
    }
    
    if (typeof obj === 'object') {
      const method = path[path.length - 1];
      const pathName = path[path.length - 2]?.replace(/[^a-zA-Z0-9]/g, '');
      obj.operationId = method + pathName.charAt(0).toUpperCase() + pathName.slice(1);
    }
    
    return format === 'json' ? JSON.stringify(parsed, null, 2) : yaml.dump(parsed, { indent: 2, noRefs: true });
  } catch (e) {
    return content;
  }
}

function addExampleAtPath(content, path, format) {
  try {
    const parsed = format === 'json' ? JSON.parse(content) : yaml.load(content);
    let obj = parsed;
    
    for (let i = 0; i < path.length; i++) {
      obj = obj[path[i]];
      if (!obj) return content;
    }
    
    if (typeof obj === 'object' && obj.type) {
      if (obj.type === 'string') obj.example = 'example';
      else if (obj.type === 'number' || obj.type === 'integer') obj.example = 0;
      else if (obj.type === 'boolean') obj.example = true;
      else if (obj.type === 'array') obj.example = [];
      else if (obj.type === 'object') obj.example = {};
    }
    
    return format === 'json' ? JSON.stringify(parsed, null, 2) : yaml.dump(parsed, { indent: 2, noRefs: true });
  } catch (e) {
    return content;
  }
}

function addResponseDescriptionAtPath(content, path, format) {
  try {
    const parsed = format === 'json' ? JSON.parse(content) : yaml.load(content);
    let obj = parsed;
    
    for (let i = 0; i < path.length; i++) {
      obj = obj[path[i]];
      if (!obj) return content;
    }
    
    if (typeof obj === 'object') {
      const statusCode = path[path.length - 1];
      const descriptions = {
        '200': 'Successful response',
        '201': 'Resource created',
        '204': 'No content',
        '400': 'Bad request',
        '401': 'Unauthorized',
        '403': 'Forbidden',
        '404': 'Not found',
        '500': 'Internal server error'
      };
      obj.description = descriptions[statusCode] || 'Response';
    }
    
    return format === 'json' ? JSON.stringify(parsed, null, 2) : yaml.dump(parsed, { indent: 2, noRefs: true });
  } catch (e) {
    return content;
  }
}

function addParameterDescriptionAtPath(content, path, format) {
  try {
    const parsed = format === 'json' ? JSON.parse(content) : yaml.load(content);
    let obj = parsed;
    
    for (let i = 0; i < path.length; i++) {
      obj = obj[path[i]];
      if (!obj) return content;
    }
    
    if (typeof obj === 'object' && obj.name) {
      obj.description = 'Description for ' + obj.name + ' parameter';
    }
    
    return format === 'json' ? JSON.stringify(parsed, null, 2) : yaml.dump(parsed, { indent: 2, noRefs: true });
  } catch (e) {
    return content;
  }
}

function addContactInfo(content, format) {
  try {
    const parsed = format === 'json' ? JSON.parse(content) : yaml.load(content);
    if (!parsed.info) parsed.info = {};
    parsed.info.contact = {
      name: 'API Support',
      email: 'support@example.com'
    };
    return format === 'json' ? JSON.stringify(parsed, null, 2) : yaml.dump(parsed, { indent: 2, noRefs: true });
  } catch (e) {
    return content;
  }
}

function addLicenseInfo(content, format) {
  try {
    const parsed = format === 'json' ? JSON.parse(content) : yaml.load(content);
    if (!parsed.info) parsed.info = {};
    parsed.info.license = {
      name: 'MIT',
      url: 'https://opensource.org/licenses/MIT'
    };
    return format === 'json' ? JSON.stringify(parsed, null, 2) : yaml.dump(parsed, { indent: 2, noRefs: true });
  } catch (e) {
    return content;
  }
}

function addLicenseUrl(content, format) {
  try {
    const parsed = format === 'json' ? JSON.parse(content) : yaml.load(content);
    if (parsed.info?.license && !parsed.info.license.url) {
      parsed.info.license.url = 'https://opensource.org/licenses/MIT';
    }
    return format === 'json' ? JSON.stringify(parsed, null, 2) : yaml.dump(parsed, { indent: 2, noRefs: true });
  } catch (e) {
    return content;
  }
}

function addTags(content, format) {
  try {
    const parsed = format === 'json' ? JSON.parse(content) : yaml.load(content);
    if (!parsed.tags || parsed.tags.length === 0) {
      parsed.tags = [{ name: 'default', description: 'Default operations' }];
    }
    return format === 'json' ? JSON.stringify(parsed, null, 2) : yaml.dump(parsed, { indent: 2, noRefs: true });
  } catch (e) {
    return content;
  }
}

function addOperationTags(content, path, format) {
  try {
    const parsed = format === 'json' ? JSON.parse(content) : yaml.load(content);
    let obj = parsed;
    
    for (let i = 0; i < path.length; i++) {
      obj = obj[path[i]];
      if (!obj) return content;
    }
    
    if (typeof obj === 'object' && !obj.tags) {
      obj.tags = ['default'];
    }
    
    return format === 'json' ? JSON.stringify(parsed, null, 2) : yaml.dump(parsed, { indent: 2, noRefs: true });
  } catch (e) {
    return content;
  }
}

function addTagDescription(content, path, format) {
  try {
    const parsed = format === 'json' ? JSON.parse(content) : yaml.load(content);
    let obj = parsed;
    
    for (let i = 0; i < path.length; i++) {
      obj = obj[path[i]];
      if (!obj) return content;
    }
    
    if (typeof obj === 'object' && obj.name && !obj.description) {
      obj.description = `Operations related to ${obj.name}`;
    }
    
    return format === 'json' ? JSON.stringify(parsed, null, 2) : yaml.dump(parsed, { indent: 2, noRefs: true });
  } catch (e) {
    return content;
  }
}

function removeTrailingSlash(content, path, format) {
  try {
    const parsed = format === 'json' ? JSON.parse(content) : yaml.load(content);
    if (parsed.paths) {
      const newPaths = {};
      Object.keys(parsed.paths).forEach(p => {
        const newPath = p.endsWith('/') && p !== '/' ? p.slice(0, -1) : p;
        newPaths[newPath] = parsed.paths[p];
      });
      parsed.paths = newPaths;
    }
    return format === 'json' ? JSON.stringify(parsed, null, 2) : yaml.dump(parsed, { indent: 2, noRefs: true });
  } catch (e) {
    return content;
  }
}

function removeServerTrailingSlash(content, path, format) {
  try {
    const parsed = format === 'json' ? JSON.parse(content) : yaml.load(content);
    if (parsed.servers) {
      parsed.servers.forEach(server => {
        if (server.url && server.url.endsWith('/')) {
          server.url = server.url.slice(0, -1);
        }
      });
    }
    return format === 'json' ? JSON.stringify(parsed, null, 2) : yaml.dump(parsed, { indent: 2, noRefs: true });
  } catch (e) {
    return content;
  }
}

function addServers(content, format) {
  try {
    const parsed = format === 'json' ? JSON.parse(content) : yaml.load(content);
    if (!parsed.servers || parsed.servers.length === 0) {
      parsed.servers = [{ url: 'https://api.example.com', description: 'Production server' }];
    }
    return format === 'json' ? JSON.stringify(parsed, null, 2) : yaml.dump(parsed, { indent: 2, noRefs: true });
  } catch (e) {
    return content;
  }
}

function addSuccessResponse(content, path, format) {
  try {
    const parsed = format === 'json' ? JSON.parse(content) : yaml.load(content);
    let obj = parsed;
    
    for (let i = 0; i < path.length; i++) {
      obj = obj[path[i]];
      if (!obj) return content;
    }
    
    if (typeof obj === 'object' && obj.responses) {
      const hasSuccess = Object.keys(obj.responses).some(code => 
        code.startsWith('2') || code.startsWith('3')
      );
      if (!hasSuccess) {
        obj.responses['200'] = { description: 'Successful response' };
      }
    }
    
    return format === 'json' ? JSON.stringify(parsed, null, 2) : yaml.dump(parsed, { indent: 2, noRefs: true });
  } catch (e) {
    return content;
  }
}